import { AuthShellTone, AuthStatusView } from '@today/ui-domain'
import { useTranslation } from 'react-i18next'
import { useAuthLogo } from '@/auth/useAuthLogo'
import { AUTH_NAMESPACE } from '@/i18n'

/**
 * The kinds of the full-page HTTP error family (TO-640 http-error-pages,
 * ux-interaction-standards §12). Each value is also the kind's `/error/<slug>`.
 */
export enum HttpErrorKind {
	BadRequest = 'bad-request',
	Unauthorized = 'unauthorized',
	Forbidden = 'forbidden',
	NotFound = 'not-found',
	ServerError = 'server-error',
	Unavailable = 'unavailable',
	/** Any failure that is not one of the statuses above, including a non-HTTP error. */
	Unexpected = 'unexpected',
}

/**
 * How one kind renders — the spec's Kind table. `retryable` kinds offer "Try
 * again" when shown in place of a failed route; a standalone `/error/*` route
 * has nothing to retry, so it never passes a retry handler.
 */
interface HttpErrorKindConfig {
	copyKey: string
	tone: AuthShellTone
	actionLabelKey: string
	href: string
	retryable: boolean
}

const TO_DASHBOARD = { actionLabelKey: 'status.goToDashboard', href: '/dashboard' }

const KINDS: Record<HttpErrorKind, HttpErrorKindConfig> = {
	[HttpErrorKind.BadRequest]: { copyKey: 'badRequest', tone: AuthShellTone.Warning, ...TO_DASHBOARD, retryable: false },
	[HttpErrorKind.Unauthorized]: {
		copyKey: 'unauthorized',
		tone: AuthShellTone.Error,
		actionLabelKey: 'status.backToLogin',
		href: '/login',
		retryable: false,
	},
	// `forbidden.action` (not the shared label) is pinned by login-surface REQ-011 (TO-738); same text.
	[HttpErrorKind.Forbidden]: {
		copyKey: 'forbidden',
		tone: AuthShellTone.Error,
		actionLabelKey: 'forbidden.action',
		href: '/dashboard',
		retryable: false,
	},
	[HttpErrorKind.NotFound]: { copyKey: 'notFound', tone: AuthShellTone.Warning, ...TO_DASHBOARD, retryable: false },
	[HttpErrorKind.ServerError]: { copyKey: 'serverError', tone: AuthShellTone.Error, ...TO_DASHBOARD, retryable: true },
	[HttpErrorKind.Unavailable]: {
		copyKey: 'unavailable',
		tone: AuthShellTone.Warning,
		...TO_DASHBOARD,
		retryable: true,
	},
	[HttpErrorKind.Unexpected]: { copyKey: 'unexpected', tone: AuthShellTone.Error, ...TO_DASHBOARD, retryable: true },
}

/** The HTTP statuses that have their own kind; every other status is {@link HttpErrorKind.Unexpected}. */
const KIND_BY_STATUS: ReadonlyMap<number, HttpErrorKind> = new Map([
	[400, HttpErrorKind.BadRequest],
	[401, HttpErrorKind.Unauthorized],
	[403, HttpErrorKind.Forbidden],
	[404, HttpErrorKind.NotFound],
	[500, HttpErrorKind.ServerError],
	[503, HttpErrorKind.Unavailable],
])

/** The family kind that displays an HTTP `status` (REQ-003). */
export function httpErrorKindForStatus(status: number): HttpErrorKind {
	return KIND_BY_STATUS.get(status) ?? HttpErrorKind.Unexpected
}

/**
 * Props for {@link HttpErrorPage}.
 *
 * @property kind - Which error this page shows.
 * @property reason - A reference code the caller has already narrowed to an app-defined constant.
 * @property onRetry - Reloads the failed route; honoured only by retryable kinds.
 */
export interface HttpErrorPageProps {
	kind: HttpErrorKind
	reason?: string | null
	onRetry?: () => void
}

/**
 * One full-page error of the HTTP family (TO-640 http-error-pages REQ-001):
 * the shared status card with the kind's tone, localised copy and recovery
 * action. It renders no status number, path or error detail (REQ-004).
 */
export function HttpErrorPage({ kind, reason, onRetry }: HttpErrorPageProps) {
	const { t } = useTranslation(AUTH_NAMESPACE)
	const logo = useAuthLogo()
	const config = KINDS[kind]
	const retry = config.retryable && onRetry ? { label: t('status.tryAgain'), onRetry } : undefined

	return (
		<AuthStatusView
			logo={logo}
			tone={config.tone}
			title={t(`${config.copyKey}.title`)}
			message={t(`${config.copyKey}.message`)}
			reason={reason ? { label: t('status.reasonLabel'), code: reason } : null}
			action={{ label: t(config.actionLabelKey), href: config.href }}
			retry={retry}
		/>
	)
}
